// src/hooks/useAuth.js
import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signInWithPopup,
  signInWithRedirect, getRedirectResult,
  signOut, updateProfile, sendEmailVerification
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';
import { createUserDoc, claimVerifiedAgreements } from '../firebase/services';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  getRedirectResult(auth).then(async (result) => {
    if (result?.user) {
      await createUserDoc(result.user.uid, {
        email: result.user.email,
        displayName: result.user.displayName,
      });
      claimVerifiedAgreements().catch(err => console.error('[ClaimAgreements] redirect result:', err));
    }
  }).catch(err => console.error('[Auth] getRedirectResult:', err));

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    claimVerifiedAgreements().catch(err => console.error('[ClaimAgreements] login:', err));
    return cred;
  };

  const signup = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    await sendEmailVerification(cred.user);
    await createUserDoc(cred.user.uid, { email, displayName });
    claimVerifiedAgreements().catch(err => console.error('[ClaimAgreements] signup:', err));
    return cred;
  };

  const loginWithGoogle = async () => {
    const isNative = window.Capacitor?.isNativePlatform?.() || false;
    if (isNative) {
      const { Browser } = await import('@capacitor/browser');
      const { App } = await import('@capacitor/app');
      const { GoogleAuthProvider, signInWithCredential } = await import('firebase/auth');

      const clientId = '703756924772-jtkugober1neoaqa3ifaam7d59cthndr.apps.googleusercontent.com';
      const redirectUri = `com.googleusercontent.apps.703756924772-jtkugober1neoaqa3ifaam7d59cthndr:/`;
      const scope = 'openid profile email';
      const nonce = Math.random().toString(36).substring(2);

      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'id_token',
        scope: scope,
        nonce: nonce,
        prompt: 'select_account'
      });

      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;

      return new Promise(async (resolve, reject) => {
        const listener = await App.addListener('appUrlOpen', async (data) => {
          listener.remove();
          await Browser.close();
          try {
            const url = new URL(data.url);
            const hash = new URLSearchParams(url.hash.substring(1));
            const idToken = hash.get('id_token');
            const accessToken = hash.get('access_token');
            if (!idToken) throw new Error('No id_token received');
            const credential = GoogleAuthProvider.credential(idToken, accessToken);
            const cred = await signInWithCredential(auth, credential);
            await createUserDoc(cred.user.uid, {
              email: cred.user.email,
              displayName: cred.user.displayName,
            });
            claimVerifiedAgreements().catch(err => console.error('[ClaimAgreements] google native:', err));
            resolve(cred);
          } catch (err) {
            reject(err);
          }
        });
        await Browser.open({ url: authUrl });
      });
    } else {
      const cred = await signInWithPopup(auth, googleProvider);
      await createUserDoc(cred.user.uid, {
        email: cred.user.email,
        displayName: cred.user.displayName,
      });
      claimVerifiedAgreements().catch(err => console.error('[ClaimAgreements] google web:', err));
      // Flag if user needs to set a password (Google-only provider)
      const needsPassword = cred.user.providerData.length === 1
        && cred.user.providerData[0].providerId === 'google.com';
      return { ...cred, needsPassword };
    }
  };

  const logout = () => signOut(auth);

  const resendVerification = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      await sendEmailVerification(auth.currentUser);
    }
  };

  const refreshUser = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      setUser(auth.currentUser);
      return auth.currentUser;
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, loginWithGoogle, logout, resendVerification, refreshUser }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
